/**
 * Firestore Retry and Fallback Utilities
 * Handles ERR_BLOCKED_BY_CLIENT and other network issues
 */

import { doc, addDoc, updateDoc, deleteDoc, getDoc, setDoc, collection } from 'firebase/firestore';
import { db } from './firebase';

interface RetryOptions {
  maxRetries?: number;
  delay?: number;
  exponentialBackoff?: boolean;
}

const DEFAULT_OPTIONS: RetryOptions = {
  maxRetries: 3,
  delay: 1000,
  exponentialBackoff: true
};

/**
 * Retry wrapper for Firestore operations
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= opts.maxRetries!; attempt++) {
    try {
      return await operation();
    } catch (error: any) {
      lastError = error;
      
      // Check if it's a blocking error
      if (isBlockingError(error)) {
        console.warn(`Firestore operation blocked (attempt ${attempt + 1}/${opts.maxRetries! + 1}):`, error.message);
        
        if (attempt < opts.maxRetries!) {
          const delay = opts.exponentialBackoff 
            ? opts.delay! * Math.pow(2, attempt)
            : opts.delay!;
          
          console.log(`Retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
      }
      
      // If not a blocking error or max retries reached, throw immediately
      throw error;
    }
  }

  throw lastError;
}

/**
 * Check if error is due to client-side blocking or network issues
 */
function isBlockingError(error: any): boolean {
  const message = error?.message?.toLowerCase() || '';
  const code = error?.code?.toLowerCase() || '';
  const errorString = JSON.stringify(error).toLowerCase();
  
  return (
    message.includes('blocked') ||
    message.includes('network') ||
    message.includes('failed to fetch') ||
    message.includes('quic') ||
    message.includes('webchannel') ||
    errorString.includes('err_quic_protocol_error') ||
    errorString.includes('quic_too_many_rtos') ||
    code.includes('unavailable') ||
    code.includes('deadline-exceeded') ||
    code === 'cancelled'
  );
}

/**
 * Fallback to localStorage when Firestore is blocked
 */
export class FirestoreFallback {
  public static STORAGE_PREFIX = 'vernikaai_fallback_';

  static async saveToLocalStorage(key: string, data: any): Promise<void> {
    try {
      const fullKey = `${this.STORAGE_PREFIX}${key}`;
      localStorage.setItem(fullKey, JSON.stringify({
        data,
        timestamp: Date.now(),
        source: 'fallback'
      }));
      console.log(`Saved to localStorage fallback: ${key}`);
    } catch (error) {
      console.error('Failed to save to localStorage:', error);
    }
  }

  static async loadFromLocalStorage(key: string): Promise<any> {
    try {
      const fullKey = `${this.STORAGE_PREFIX}${key}`;
      const stored = localStorage.getItem(fullKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.data;
      }
      return null;
    } catch (error) {
      console.error('Failed to load from localStorage:', error);
      return null;
    }
  }

  static async getAllFromLocalStorage(prefix: string): Promise<any[]> {
    try {
      const results: any[] = [];
      const fullPrefix = `${this.STORAGE_PREFIX}${prefix}`;
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(fullPrefix)) {
          const stored = localStorage.getItem(key);
          if (stored) {
            const parsed = JSON.parse(stored);
            results.push(parsed.data);
          }
        }
      }
      
      return results;
    } catch (error) {
      console.error('Failed to load all from localStorage:', error);
      return [];
    }
  }

  static clearLocalStorage(prefix?: string): void {
    try {
      const fullPrefix = prefix ? `${this.STORAGE_PREFIX}${prefix}` : this.STORAGE_PREFIX;
      
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(fullPrefix)) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      console.log(`Cleared localStorage fallback: ${prefix || 'all'}`);
    } catch (error) {
      console.error('Failed to clear localStorage:', error);
    }
  }
}

/**
 * Enhanced Firestore operations with fallback
 */
export class FirestoreWithFallback {
  static async addDocument(
    collectionRef: any,
    data: any,
    fallbackKey: string,
    options: RetryOptions = {}
  ): Promise<any> {
    try {
      return await withRetry(() => addDoc(collectionRef, data), options);
    } catch (error) {
      console.warn('Firestore blocked, using localStorage fallback');
      await FirestoreFallback.saveToLocalStorage(fallbackKey, data);
      
      // Return a mock document reference
      return {
        id: `fallback_${Date.now()}`,
        path: fallbackKey
      };
    }
  }

  static async updateDocument(
    docRef: any,
    data: any,
    fallbackKey: string,
    options: RetryOptions = {}
  ): Promise<void> {
    try {
      return await withRetry(() => updateDoc(docRef, data), options);
    } catch (error) {
      console.warn('Firestore blocked, using localStorage fallback');
      const existing = await FirestoreFallback.loadFromLocalStorage(fallbackKey);
      const updated = { ...existing, ...data };
      await FirestoreFallback.saveToLocalStorage(fallbackKey, updated);
    }
  }

  static async deleteDocument(
    docRef: any,
    fallbackKey: string,
    options: RetryOptions = {}
  ): Promise<void> {
    try {
      return await withRetry(() => deleteDoc(docRef), options);
    } catch (error) {
      console.warn('Firestore blocked, using localStorage fallback');
      localStorage.removeItem(`${FirestoreFallback.STORAGE_PREFIX}${fallbackKey}`);
    }
  }

  static async getDocument(
    docRef: any,
    fallbackKey: string,
    options: RetryOptions = {}
  ): Promise<any> {
    try {
      return await withRetry(() => getDoc(docRef), options);
    } catch (error) {
      console.warn('Firestore blocked, using localStorage fallback');
      const data = await FirestoreFallback.loadFromLocalStorage(fallbackKey);
      return {
        exists: () => !!data,
        data: () => data,
        id: fallbackKey
      };
    }
  }
}
